import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { Card, Divider } from 'antd';
import { useUpdateBreadcrumbParam } from '@/contexts/BreadcrumbContextProvider';
import { quantityFormatter } from '@/utils/number';
import { PageProps } from '@/types/PageProps';
import {
  Table,
  ColumnsType,
  DefaultRouteTabs,
  SaveButton,
} from '@/components/_common';
import { getTableScrollX } from '@/utils/table';
import {
  LastConsumablePurchasesRequest,
  LastItemPurchases,
  LastItemPurchasesOrderColumn,
} from '@/core/domain/purchase/Purchase.types';
import { useLastConsumablesPurchases } from '@/core/domain/purchase/Purchase.hooks';
import { useConsumable } from '@/core/domain/operational/Consumables/Consumables.hooks';
import ConsumableDetailsDescription from '@/components/Operational/Inventory/Consumables/ConsumableDetailsDescription';
import moment from 'moment';
import { useConsumableFormContext } from '../ConsumableProvider';
import ConsumableFooter from '@/components/Operational/Inventory/Consumables/ConsumableFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useState } from 'react';
import { useDebounce } from '@/hooks';
import { PurchaseDateOrderSelector } from '@/components/_common/PurchaseDateOrderSelector';

function ConsumablesLastPurchasesPage({ route }: PageProps) {
  const intl = useIntl();
  const {
    consumableId,
    isLoading: contextLoading,
    handleSubmit,
    disabled,
  } = useConsumableFormContext();

  const { data: consumable, isLoading } = useConsumable(Number(consumableId));

  useUpdateBreadcrumbParam(':consumableId', consumable?.name);

  const [queryParams, setQueryParams] =
    useState<LastConsumablePurchasesRequest>({
      consumableId: Number(consumableId),
      orderByColumn: LastItemPurchasesOrderColumn.RELEASE_DATE,
    });

  const queryDebounce = useDebounce({ ...queryParams }, 500);
  const { data, isLoading: isLoadingConsumables } =
    useLastConsumablesPurchases(queryDebounce);

  const columns: ColumnsType<LastItemPurchases> = [
    {
      dataIndex: 'code',
      title: intl.formatMessage({
        id: 'consumables.lastPurchases.domain.code',
      }),
      ellipsis: true,
      width: 90,
    },
    {
      dataIndex: 'supplier',
      title: intl.formatMessage({
        id: 'consumables.lastPurchases.domain.supplier',
      }),
      width: 230,
    },
    {
      dataIndex: 'issueDate',
      title: intl.formatMessage({
        id: 'parts.lastPurchases.domain.issueDate',
      }),
      width: 150,
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:MM') : '-',
    },
    {
      dataIndex: 'releaseDate',
      title: intl.formatMessage({
        id: 'parts.lastPurchases.domain.releaseDate',
      }),
      width: 150,
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:MM') : '-',
    },
    {
      dataIndex: 'quantity',
      title: intl.formatMessage({
        id: 'consumables.lastPurchases.domain.quantity',
      }),
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    {
      dataIndex: 'unitaryValue',
      title: intl.formatMessage({
        id: 'consumables.lastPurchases.domain.unitaryValue',
      }),
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    {
      dataIndex: 'totalAmountItem',
      title: intl.formatMessage({
        id: 'consumables.lastPurchases.domain.totalAmountItem',
      }),
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        route={route.parent}
        baseKey={consumableId}
        basePath={`/app/operational/inventory/consumables/${consumableId}`}
        extra={
          <SaveButton
            block
            onClick={handleSubmit}
            loading={contextLoading}
            disabled={
              disabled ||
              !hasPermissions([Authorities.REGISTER_CONSUMABLE_EDIT])
            }
          >
            {intl.formatMessage({ id: 'commons.entity.save' })}
          </SaveButton>
        }
      >
        <ConsumableDetailsDescription
          isLoading={isLoading}
          consumable={consumable}
        />

        <Divider />

        <PurchaseDateOrderSelector
          defaultValue={queryParams.orderByColumn}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, orderByColumn: value }))
          }
        />

        <Table<LastItemPurchases>
          columns={columns}
          dataSource={data}
          loading={isLoading || isLoadingConsumables}
          pagination={false}
          rowKey={(row) => row.id}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <Divider />

        <Card>
          <strong>
            {intl.formatMessage({
              id: 'consumables.lastPurchases.domain.averageCostLastPurchases',
            })}
            {quantityFormatter(
              data
                ? data.reduce((acc, curr) => (acc += curr.totalAmountItem), 0) /
                    data.length
                : 0,
            )}
          </strong>
        </Card>

        <ConsumableFooter disabled={disabled} />
      </DefaultRouteTabs>
    </>
  );
}

export default ConsumablesLastPurchasesPage;
